import type { BenefitResult } from '@/types';

export type HouseholdType = 'single' | 'couple' | 'single-with-children' | 'couple-with-children';
export type HousingStatus = 'renting' | 'owning' | 'social-housing';
export type EmploymentStatus = 'employed' | 'self-employed' | 'mixed' | 'not-working';
export type BenefitsLifeEvent =
  | 'Changed job'
  | 'Moved house'
  | 'New baby'
  | 'Separated'
  | "Child's birthday"
  | 'Redundancy'
  | 'Bereavement'
  | 'New diagnosis';

export interface BenefitsInput {
  postcode: string;
  income: number;
  householdType: HouseholdType;
  childAges: number[];
  housingStatus: HousingStatus;
  employmentStatus: EmploymentStatus;
  hasHealthCondition: boolean;
  providesCare: boolean;
}

export interface ExtendedBenefitResult extends BenefitResult {
  applyUrl: string;
  urgent?: boolean;
  category: 'income' | 'housing' | 'health' | 'children' | 'energy' | 'local';
  reason: string;
  valueLabel?: string;
}

export interface BenefitsLifeEventRescan {
  nextInput: BenefitsInput;
  summary: string;
  changedFields: string[];
}

function householdLabel(householdType: HouseholdType) {
  if (householdType === 'single-with-children') return 'a single-parent household';
  if (householdType === 'couple-with-children') return 'a couple household with children';
  if (householdType === 'couple') return 'a couple household';
  return 'a single-adult household';
}

function employmentLabel(employmentStatus: EmploymentStatus) {
  if (employmentStatus === 'self-employed') return 'self-employed';
  if (employmentStatus === 'mixed') return 'in a mix of employed and self-employed work';
  if (employmentStatus === 'not-working') return 'not currently working';
  return 'employed';
}

function withChildrenHouseholdType(householdType: HouseholdType) {
  if (householdType === 'single') return 'single-with-children';
  if (householdType === 'couple') return 'couple-with-children';
  return householdType;
}

function withSingleHouseholdType(householdType: HouseholdType) {
  if (householdType === 'couple') return 'single';
  if (householdType === 'couple-with-children') return 'single-with-children';
  return householdType;
}

export function applyBenefitsLifeEvent(input: BenefitsInput, event: BenefitsLifeEvent): BenefitsLifeEventRescan {
  const nextInput: BenefitsInput = {
    ...input,
    childAges: [...input.childAges],
  };
  const changedFields: string[] = [];
  let summary = 'Benefits re-scanned with your saved answers. Review the details below if this life event changed income, housing, or care needs too.';

  switch (event) {
    case 'New baby': {
      const nextHouseholdType = withChildrenHouseholdType(nextInput.householdType);
      if (nextHouseholdType !== nextInput.householdType) {
        nextInput.householdType = nextHouseholdType;
        changedFields.push('household updated to include children');
      }
      nextInput.childAges = [0, ...nextInput.childAges];
      changedFields.push('added a new baby to the benefits check');
      summary = 'Benefits re-scanned as a household with a new baby. Review childcare, housing, and income details as soon as you can.';
      break;
    }
    case 'Separated': {
      const nextHouseholdType = withSingleHouseholdType(nextInput.householdType);
      if (nextHouseholdType !== nextInput.householdType) {
        nextInput.householdType = nextHouseholdType;
        changedFields.push('household updated from couple to single-adult status');
      }
      summary = 'Benefits re-scanned for a separation scenario. Review income, housing, and child-arrangements details because those often change together.';
      break;
    }
    case "Child's birthday": {
      if (nextInput.childAges.length > 0) {
        nextInput.childAges = nextInput.childAges.map((age) => age + 1);
        changedFields.push('aged each saved child by one year for this re-scan');
        summary = 'Benefits re-scanned with each saved child one year older. Check funded-hours, school-age, and meal-support changes carefully.';
      } else {
        summary = 'The life event was saved, but this scan has no children recorded yet. Update your details first if the birthday changes eligibility.';
      }
      break;
    }
    case 'Redundancy': {
      if (nextInput.employmentStatus !== 'not-working') {
        nextInput.employmentStatus = 'not-working';
        changedFields.push('main work status updated to not currently working');
      }
      summary = 'Benefits re-scanned for redundancy. Update income as soon as you know what has changed, because that often shifts the result most.';
      break;
    }
    case 'New diagnosis': {
      if (!nextInput.hasHealthCondition) {
        nextInput.hasHealthCondition = true;
        changedFields.push('health-condition support was switched on for this re-scan');
      }
      summary = 'Benefits re-scanned with disability and long-term-health support in scope. Check care, mobility, and NHS cost help as well as core benefits.';
      break;
    }
    case 'Changed job':
      summary = 'Benefits re-scanned and the life event was saved. If pay, hours, or work type changed, update your details so the result stays accurate.';
      break;
    case 'Moved house':
      summary = 'Benefits re-scanned and the move was saved. Update postcode and housing details if the move changed rent, council, or tenure.';
      break;
    case 'Bereavement':
      summary = 'Benefits re-scanned and the bereavement was saved. Review household, income, and caring details because they often change after a loss.';
      break;
  }

  return {
    nextInput,
    summary,
    changedFields,
  };
}

export function calculateBenefits(input: BenefitsInput): ExtendedBenefitResult[] {
  const {
    income,
    householdType,
    childAges,
    housingStatus,
    employmentStatus,
    hasHealthCondition,
    providesCare,
  } = input;
  const results: ExtendedBenefitResult[] = [];
  const hasChildren = householdType.includes('children');
  const isSingle = householdType.startsWith('single');
  const numChildren = childAges.length;
  const isWorking = employmentStatus !== 'not-working';
  const hasYoungChild = childAges.some((age) => age < 4);
  const hasChildUnder16 = childAges.some((age) => age < 16);
  const hasSchoolAgeChild = childAges.some((age) => age >= 4 && age <= 16);
  const hasFundedHoursAgeChild = childAges.some((age) => age >= 9 / 12 && age <= 4);
  const householdSummary = `${householdLabel(householdType)} and said the main work status is ${employmentLabel(employmentStatus)}`;

  // Universal Credit
  const ucThreshold = hasChildren ? 30000 : isSingle ? 20000 : 25000;
  if (income < ucThreshold) {
    const baseAmount = isSingle ? 4620 : 7260;
    const childElement = numChildren * 3235;
    results.push({
      name: 'Universal Credit',
      annualValue: baseAmount + (hasChildren ? childElement : 0),
      status: income < ucThreshold * 0.7 ? 'eligible' : 'check',
      description: 'A monthly payment to help with living costs. Replaces several older benefits and is the main income-related benefit in the UK.',
      applyUrl: 'https://www.gov.uk/universal-credit/how-to-claim',
      category: 'income',
      reason: `Your income looks low enough for ${householdSummary}, so the main income-related benefit is worth checking.`,
    });
  }

  // Housing Benefit / UC Housing Element
  if (housingStatus === 'renting' || housingStatus === 'social-housing') {
    const housingValue = housingStatus === 'social-housing' ? 3000 : 4500;
    results.push({
      name: 'Housing Benefit',
      annualValue: housingValue,
      status: income < 25000 ? 'eligible' : income < 35000 ? 'check' : 'not-eligible',
      description: 'Help with rent payments if you\'re on a low income. Usually paid as part of Universal Credit for new claims.',
      applyUrl: 'https://www.gov.uk/housing-benefit/how-to-claim',
      category: 'housing',
      reason: `You said you ${housingStatus === 'social-housing' ? 'live in social or council housing' : 'rent your home'}, so rent support should stay on the list.`,
    });
  }

  // Council Tax Reduction
  if (income < 30000) {
    const ctValue = isSingle ? 1000 : 800;
    results.push({
      name: 'Council Tax Reduction',
      annualValue: ctValue,
      status: income < 16000 ? 'eligible' : 'check',
      description: 'A discount on your council tax bill — could be up to 100% off. Varies by local council, so it\'s always worth checking.',
      applyUrl: 'https://www.gov.uk/council-tax-reduction',
      category: 'housing',
      reason: `Your income is in the range where council tax help is often worth checking, especially alongside rent or child costs.`,
    });
  }

  // Child Benefit
  if (hasChildren && numChildren > 0) {
    const firstChild = 1331;
    const additionalChildren = Math.max(0, numChildren - 1) * 881;
    results.push({
      name: 'Child Benefit',
      annualValue: firstChild + additionalChildren,
      status: income < 60000 ? 'eligible' : income < 80000 ? 'check' : 'not-eligible',
      description: 'A regular payment for each child you\'re responsible for. Higher earners may need to repay some through the High Income Child Benefit Charge.',
      applyUrl: 'https://www.gov.uk/child-benefit/how-to-claim',
      category: 'children',
      reason: `You said there ${numChildren === 1 ? 'is 1 child' : `are ${numChildren} children`} in the household, so Child Benefit should be checked against the higher-income charge rules.`,
    });
  }

  // Tax-Free Childcare
  if (hasChildren && childAges.some((age) => age < 12)) {
    const eligibleChildren = childAges.filter((age) => age < 12).length;
    results.push({
      name: 'Tax-Free Childcare',
      annualValue: eligibleChildren * 2000,
      status: income > 100000 ? 'not-eligible' : isWorking && income > 0 ? 'eligible' : 'check',
      description: 'The government tops up your childcare payments by 20%. You get up to £2,000 per child per year — like a £500 bonus every quarter.',
      applyUrl: 'https://www.gov.uk/tax-free-childcare',
      category: 'children',
      reason: isWorking
        ? `You said someone in the household is working and there ${eligibleChildren === 1 ? 'is a child' : 'are children'} under 12, so the childcare top-up is worth checking.`
        : 'You said there are children under 12. Tax-Free Childcare usually needs working parents, so this stays as a check rather than a straight yes.',
    });
  }

  // Free Childcare Hours (15/30 hours)
  if (hasChildren && hasFundedHoursAgeChild) {
    const hasThreeOrFourYearOld = childAges.some((age) => age >= 3 && age <= 4);
    results.push({
      name: 'Free Childcare Hours',
      annualValue: 5000,
      status: hasThreeOrFourYearOld || isWorking ? 'eligible' : 'check',
      description: 'Free early education for children aged 9 months to 4. You could get 15 or 30 hours per week depending on your situation.',
      applyUrl: 'https://www.gov.uk/help-with-childcare-costs/free-childcare-and-education-for-2-to-4-year-olds',
      urgent: true,
      category: 'children',
      reason: hasThreeOrFourYearOld
        ? 'You said there is a child in the 3-to-4 range, so funded hours should be checked now.'
        : 'You said there is a younger child in the funded-hours age range, so this is worth checking against the current work and income rules.',
    });
  }

  // Free School Meals
  if (hasChildren && hasSchoolAgeChild) {
    results.push({
      name: 'Free School Meals',
      annualValue: numChildren * 500,
      status: income < 7400 ? 'eligible' : income < 16190 ? 'check' : 'not-eligible',
      description: 'Free lunches during term time for school-age children. Also unlocks extra school funding (Pupil Premium) for your child.',
      applyUrl: 'https://www.gov.uk/apply-free-school-meals',
      category: 'children',
      reason: 'You said there are school-age children in the household, so meal support and linked school funding are worth checking against income rules.',
    });
  }

  // Healthy Start
  if (hasChildren && hasYoungChild && income < 16190) {
    const under1 = childAges.filter((age) => age < 1).length;
    const under4 = childAges.filter((age) => age >= 1 && age < 4).length;
    results.push({
      name: 'Healthy Start Vouchers',
      annualValue: (under1 * 520) + (under4 * 220),
      status: 'eligible',
      description: 'Weekly vouchers for milk, fresh fruit, veg, and vitamins. Worth £4.25/week per child (£8.50 for babies under 1).',
      applyUrl: 'https://www.healthystart.nhs.uk/',
      urgent: true,
      category: 'children',
      reason: 'You said there is a child under 4 and household income is in the lower range, which is exactly when Healthy Start becomes relevant.',
    });
  }

  // Working Tax Credit (legacy, still claimed by some)
  if (income < 20000 && income > 0 && isWorking) {
    results.push({
      name: 'Working Tax Credit',
      annualValue: 2280,
      status: 'check',
      description: 'Extra money for working people on low incomes. Most new claims now go through Universal Credit, but it\'s worth checking if you already receive it.',
      applyUrl: 'https://www.gov.uk/working-tax-credit',
      category: 'income',
      reason: `You said the household is ${employmentLabel(employmentStatus)} and income is still low, so legacy tax-credit rules are worth checking if an older award is involved.`,
    });
  }

  // Disability support
  if (hasHealthCondition) {
    results.push({
      name: 'Adult disability benefit route (PIP / Attendance Allowance)',
      annualValue: 3900,
      status: 'check',
      description: 'If a disability or long-term health condition affects daily living or mobility, the main adult route is usually PIP before State Pension age and Attendance Allowance later on. These routes are not means-tested.',
      applyUrl: 'https://www.gov.uk/financial-help-disabled',
      category: 'health',
      reason: 'You said a disability or long-term health condition affects daily life, so the main adult disability route should be checked directly instead of being hidden inside a generic support card.',
    });

    if (hasChildren && hasChildUnder16) {
      results.push({
        name: 'Disability Living Allowance (DLA) for children',
        annualValue: 3900,
        status: 'check',
        description: 'If a child under 16 needs substantially more care, supervision, or mobility support than other children of the same age, child DLA may apply. In Scotland the equivalent route is Child Disability Payment.',
        applyUrl: 'https://www.gov.uk/disability-living-allowance-children',
        category: 'children',
        reason: 'You said there is a child under 16 in the household and disability or health needs matter here, so the child-disability route is worth checking separately from the adult route.',
      });
    }
  }

  // Carer support
  if (providesCare) {
    results.push({
      name: 'Carer\'s Allowance',
      annualValue: 4300,
      status: 'check',
      description: 'If you regularly care for a disabled or unwell person, extra money may be available. This usually depends on the care being regular and the person you care for getting a qualifying disability benefit.',
      applyUrl: 'https://www.gov.uk/carers-allowance',
      category: 'income',
      reason: 'You said you regularly care for a disabled or unwell person, so the main carer-payment route is worth checking directly rather than being blended into a generic carer result.',
    });

    results.push({
      name: 'Carer\'s Credit',
      annualValue: 0,
      valueLabel: 'NI protection',
      status: 'check',
      description: 'Carer’s Credit protects your National Insurance record when caring affects your work pattern. It is often the right route when Carer’s Allowance is not payable.',
      applyUrl: 'https://www.gov.uk/carers-credit',
      category: 'income',
      reason: 'You said you regularly care for someone, so NI protection should stay visible too, especially if work or earnings make Carer’s Allowance less straightforward.',
    });
  }

  // ESA / UC health element
  if (hasHealthCondition && employmentStatus !== 'employed') {
    results.push({
      name: 'ESA / UC health element',
      annualValue: 4800,
      status: 'check',
      description: 'If health affects your ability to work, New Style ESA or the Universal Credit health element may apply. The exact route depends on your work record and current claim.',
      applyUrl: 'https://www.gov.uk/employment-support-allowance',
      category: 'income',
      reason: `You said health affects the household and the main work status is ${employmentLabel(employmentStatus)}, so support linked to reduced capacity for work is worth checking.`,
    });
  }

  // Free Prescriptions
  const freeRxEligible = income < 16000 || (hasChildren && childAges.some(a => a < 1));
  results.push({
    name: 'Free NHS Prescriptions',
    annualValue: 120,
    status: freeRxEligible ? 'eligible' : hasHealthCondition || income < 25000 ? 'check' : 'not-eligible',
    description: 'You may not need to pay the £9.90 prescription charge. Many people qualify without realising — including on low income, certain conditions, or if pregnant.',
    applyUrl: 'https://www.gov.uk/get-a-prescription-prepayment-certificate',
    category: 'health',
    reason: freeRxEligible
      ? 'Your income and household details put you in the range where free or reduced prescription costs are commonly worth checking.'
      : hasHealthCondition
        ? 'You said health conditions matter here, so NHS charge help is still worth checking even if income alone is not the full story.'
        : 'This usually turns on low income, pregnancy, or specific conditions, so it stays as a check rather than a straight yes.',
  });

  // NHS Low Income Scheme
  if (income < 30000 || hasHealthCondition) {
    results.push({
      name: 'NHS Low Income Scheme',
      annualValue: 300,
      status: income < 16000 ? 'eligible' : 'check',
      description: 'Help with costs of NHS dental treatment, eye tests, glasses, travel to hospital, and wigs/fabric supports. A real money-saver if you need regular care.',
      applyUrl: 'https://www.gov.uk/nhs-low-income-scheme-background',
      category: 'health',
      reason: hasHealthCondition
        ? 'You said health needs are in the mix, so wider NHS travel, dental, and optical help is worth checking as well as prescriptions.'
        : 'Your income is in the range where NHS help with treatment and travel costs is worth checking.',
    });
  }

  // Warm Home Discount
  if (income < 20000 || (housingStatus === 'social-housing' && income < 30000)) {
    results.push({
      name: 'Warm Home Discount',
      annualValue: 150,
      status: income < 16000 ? 'eligible' : 'check',
      description: 'A one-off £150 discount on your electricity bill during winter. You might get it automatically, or you may need to apply with your energy supplier.',
      applyUrl: 'https://www.gov.uk/the-warm-home-discount-scheme',
      urgent: true,
      category: 'energy',
      reason: housingStatus === 'social-housing'
        ? 'You said you live in social or council housing and income is in range, so energy-bill support is worth checking.'
        : 'Your income is low enough that winter energy support should stay visible.',
    });
  }

  // Cold Weather Payment
  if (income < 20000) {
    results.push({
      name: 'Cold Weather Payment',
      annualValue: 75,
      status: 'check',
      description: '£25 for each 7-day period of very cold weather. Paid automatically if you receive certain benefits — no need to apply.',
      applyUrl: 'https://www.gov.uk/cold-weather-payment',
      category: 'energy',
      reason: 'Your income sits in the range where linked winter support can matter if a qualifying benefit is in payment.',
    });
  }

  // Local Council Grants
  results.push({
    name: 'Local Council Grants',
    annualValue: 500,
    status: 'check',
    description: 'Many councils offer extra support like crisis grants, food vouchers, school uniform grants, or energy bill help. These vary by area — your postcode matters.',
    applyUrl: 'https://www.gov.uk/find-local-council',
    category: 'local',
    reason: input.postcode
      ? `You entered ${input.postcode.toUpperCase()}, so local council help is worth checking alongside national support.`
      : 'Local councils often hold extra hardship, school, or energy support that never shows up in national lists.',
  });

  return results;
}
